<template>
  <div>
    <b-modal
      v-model="showModal"
      size="lg"
      id="crtcreator-content-modal"
      :title="edited.id ? 'Edit content' : 'Add content'"
      :ok-title="isFormLoading ? 'Saving...' : 'Save'"
      :ok-disabled="isFormLoading"
      cancel-variant="outline-secondary"
      @ok="onSubmit($event)"
    >
      <b-form>
        <b-form-group label="Name" label-for="content-name">
          <b-form-input id="content-name" v-model="edited.name" placeholder="Name" />
          <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
        </b-form-group>
        <b-form-group label="Social resource type" label-for="content-social-type">
          <ImageTextSelect
            id="content-social-type"
            v-model="edited.social_resource_type_id"
            :options="socialResourceTypes"
            image-key="icon"
            placeholder="Select type"
          />
          <small v-if="errors.social_resource_type_id" class="text-danger">{{ errors.social_resource_type_id }}</small>
        </b-form-group>
        <b-form-group label="Channel" label-for="content-channel">
          <div class="d-flex align-items-center content-channel-row">
            <div class="content-channel-select">
              <v-select
                id="content-channel"
                v-model="edited.crtcreator_channel_id"
                :options="channels || []"
                label="name"
                :reduce="(channel) => channel.id"
                :clearable="true"
                placeholder="Start typing channel name"
              />
            </div>
            <b-button
              variant="outline-primary"
              size="sm"
              class="ml-1 flex-shrink-0"
              @click="openAddChannel"
            >
              Add new channel
            </b-button>
          </div>
        </b-form-group>
        <b-form-group label="Category" label-for="content-category">
          <div class="d-flex align-items-center content-channel-row">
            <div class="content-channel-select">
              <v-select
                id="content-category"
                v-model="edited.crtcreator_content_category_id"
                :options="contentCategories || []"
                label="name"
                :reduce="(category) => category.id"
                :clearable="true"
                placeholder="Start typing category name"
              />
            </div>
            <b-button
              variant="outline-primary"
              size="sm"
              class="ml-1 flex-shrink-0"
              @click="openAddCategory"
            >
              Add new category
            </b-button>
          </div>
        </b-form-group>
        <b-form-group
          :label="edited.id ? 'ID Key' : 'ID Key *'"
          label-for="content-id-key"
        >
          <b-form-input id="content-id-key" v-model="edited.id_key" placeholder="External id" />
          <small v-if="errors.id_key" class="text-danger">{{ errors.id_key }}</small>
          <small v-else-if="idKeyChecking" class="text-muted">Checking ID key…</small>
        </b-form-group>
        <b-form-group label="Notes" label-for="content-notes">
          <b-form-textarea id="content-notes" v-model="edited.notes" rows="3" />
        </b-form-group>
      </b-form>
    </b-modal>

    <ManageChannel
      :click-date="channelClickDate"
      :record="null"
      @saved="onChannelSaved"
    />
    <ManageContentCategory
      :click-date="categoryClickDate"
      :record="null"
      @saved="onCategorySaved"
    />
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BButton, BForm, BFormGroup, BFormInput, BFormTextarea, BModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions, mapGetters } from 'vuex'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import ImageTextSelect from '@/views/Components/Elements/ImageTextSelect.vue'
import ManageChannel from '@/views/Modules/crtcreator/Components/ManageChannel.vue'
import ManageContentCategory from '@/views/Modules/crtcreator/Components/ManageContentCategory.vue'

const defaults = () => ({
  id: null,
  name: '',
  social_resource_type_id: null,
  crtcreator_channel_id: null,
  crtcreator_content_category_id: null,
  id_key: '',
  notes: '',
})

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BModal,
    ImageTextSelect,
    ManageChannel,
    ManageContentCategory,
    'v-select': vSelect,
  },
  props: {
    record: { type: Object, default: null },
    clickDate: { type: Date, default: null },
  },
  emits: ['saved'],
  data() {
    return {
      showModal: false,
      isFormLoading: false,
      edited: defaults(),
      errors: {},
      socialResourceTypes: [],
      channelClickDate: null,
      categoryClickDate: null,
      idKeyCheckTimer: null,
      idKeyChecking: false,
      idKeyCheckSeq: 0,
    }
  },
  computed: {
    ...mapGetters({
      channels: 'crtcreator/channels/items',
      contentCategories: 'crtcreator/contentCategories/items',
    }),
  },
  watch: {
    async clickDate() {
      this.clearIdKeyCheck()
      this.errors = {}
      if (!this.socialResourceTypes.length) {
        try {
          const response = await axios.get('api/social-resource-types')
          this.socialResourceTypes = response.data?.data ?? response.data ?? []
        } catch (e) {
          this.socialResourceTypes = []
        }
      }
      await this.ensureCategoriesLoaded()
      if (this.record) {
        this.edited = {
          id: this.record.id,
          name: this.record.name || '',
          social_resource_type_id: this.record.social_resource_type_id || null,
          crtcreator_channel_id: this.record.crtcreator_channel_id || null,
          crtcreator_content_category_id: this.record.crtcreator_content_category_id || null,
          id_key: this.record.id_key || '',
          notes: this.record.notes || '',
        }
      } else {
        this.edited = defaults()
      }
      this.showModal = true
    },
    'edited.id_key'(value) {
      if (!this.showModal) {
        return
      }
      this.scheduleIdKeyCheck(value)
    },
  },
  beforeDestroy() {
    this.clearIdKeyCheck()
  },
  methods: {
    ...mapActions({
      addItem: 'crtcreator/content/addItem',
      updateItem: 'crtcreator/content/updateItem',
      loadContentCategories: 'crtcreator/contentCategories/loadItems',
    }),
    async ensureCategoriesLoaded() {
      if ((this.contentCategories || []).length) {
        return
      }
      try {
        await this.loadContentCategories()
      } catch (e) {
        // Leave empty if categories cannot be loaded.
      }
    },
    clearIdKeyCheck() {
      if (this.idKeyCheckTimer) {
        clearTimeout(this.idKeyCheckTimer)
        this.idKeyCheckTimer = null
      }
      this.idKeyChecking = false
      this.idKeyCheckSeq += 1
    },
    scheduleIdKeyCheck(value) {
      if (this.idKeyCheckTimer) {
        clearTimeout(this.idKeyCheckTimer)
        this.idKeyCheckTimer = null
      }

      const idKey = String(value || '').trim()
      if (this.errors.id_key === 'Content with this ID key already exists for your company.') {
        const nextErrors = { ...this.errors }
        delete nextErrors.id_key
        this.errors = nextErrors
      }

      if (!idKey) {
        this.idKeyChecking = false
        return
      }

      // Skip check when editing and value is unchanged.
      if (this.edited.id && this.record && String(this.record.id_key || '').trim() === idKey) {
        this.idKeyChecking = false
        return
      }

      this.idKeyChecking = true
      const seq = ++this.idKeyCheckSeq
      this.idKeyCheckTimer = setTimeout(() => {
        this.checkIdKeyAvailability(idKey, seq)
      }, 400)
    },
    async checkIdKeyAvailability(idKey, seq) {
      try {
        const params = { id_key: idKey }
        if (this.edited.id) {
          params.exclude_id = this.edited.id
        }
        const { data } = await axios.get('api/modules/crtcreator/content/check-id-key', { params })
        if (seq !== this.idKeyCheckSeq) {
          return
        }
        if (data?.data?.exists) {
          this.errors = {
            ...this.errors,
            id_key: 'Content with this ID key already exists for your company.',
          }
        }
      } catch (e) {
        // Keep typing UX silent on network/validation errors; save still validates.
      } finally {
        if (seq === this.idKeyCheckSeq) {
          this.idKeyChecking = false
        }
      }
    },
    openAddChannel() {
      this.channelClickDate = new Date()
    },
    onChannelSaved(channel) {
      if (channel?.id) {
        this.edited.crtcreator_channel_id = channel.id
      }
    },
    openAddCategory() {
      this.categoryClickDate = new Date()
    },
    onCategorySaved(category) {
      if (category?.id) {
        this.edited.crtcreator_content_category_id = category.id
      }
    },
    applyServerErrors(error) {
      const payload = error?.response?.data
      if (!payload || typeof payload !== 'object') {
        return false
      }
      const next = {}
      ;['name', 'social_resource_type_id', 'crtcreator_channel_id', 'crtcreator_content_category_id', 'id_key', 'notes'].forEach((field) => {
        if (payload[field]) {
          next[field] = Array.isArray(payload[field]) ? payload[field][0] : payload[field]
        }
      })
      if (Object.keys(next).length) {
        this.errors = next
        return true
      }
      return false
    },
    async onSubmit(event) {
      event.preventDefault()
      this.clearIdKeyCheck()
      this.errors = {}
      if (!String(this.edited.name || '').trim()) {
        this.errors.name = 'Name is required'
      }
      if (!this.edited.social_resource_type_id) {
        this.errors.social_resource_type_id = 'Social resource type is required'
      }
      if (!this.edited.id && !String(this.edited.id_key || '').trim()) {
        this.errors.id_key = 'ID Key is required'
      }
      if (Object.keys(this.errors).length) return

      this.isFormLoading = true
      const payload = {
        name: this.edited.name,
        social_resource_type_id: this.edited.social_resource_type_id,
        crtcreator_channel_id: this.edited.crtcreator_channel_id || null,
        crtcreator_content_category_id: this.edited.crtcreator_content_category_id || null,
        id_key: String(this.edited.id_key || '').trim() || null,
        notes: this.edited.notes || null,
      }
      try {
        if (this.edited.id) {
          await this.updateItem({ id: this.edited.id, payload })
          toastSuccess('Successfully updated')
        } else {
          await this.addItem(payload)
          toastSuccess('Successfully added')
        }
        this.showModal = false
        this.$emit('saved')
      } catch (e) {
        if (!this.applyServerErrors(e)) {
          toastFailure('Failed to save')
        }
      } finally {
        this.isFormLoading = false
      }
    },
  },
}
</script>

<style scoped>
.content-channel-row {
  width: 100%;
}

.content-channel-select {
  width: 50%;
  min-width: 0;
}
</style>
